/**
 * `POST /job-apply/api/coupon/validate` — ¿este código existe y sigue vivo?
 *
 * Un estado y no un `throw`, por lo mismo que en `cvScoreClient`: las tres
 * respuestas se cuentan distinto delante del candidato. "No existe" es un dato
 * sobre el código (y el backend lo manda con 200: preguntar no es un error);
 * "no pudimos preguntarle a Stripe" es un problema nuestro, y ahí la salida no
 * es que se rinda sino que igual pueda pagar —el campo de cupón también existe
 * en la página de Stripe—.
 */
import { apiFetch } from '~/lib/api';
import type { ValidCoupon } from '../lib/coupon';

export type CouponState =
  | { status: 'valid'; coupon: ValidCoupon }
  /** El código no existe o ya venció. */
  | { status: 'invalid' }
  /** Stripe no contestó, o la red se cayó. No se sabe nada del código. */
  | { status: 'unavailable' };

export async function validateCoupon(code: string): Promise<CouponState> {
  let res: Response;
  try {
    res = await apiFetch('/job-apply/api/coupon/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
  } catch {
    return { status: 'unavailable' };
  }

  const body = (await res.json().catch(() => null)) as (Omit<Partial<ValidCoupon>, 'partner'> & {
    valid?: boolean;
    partner?: { code?: unknown; partner_name?: unknown } | null;
  }) | null;

  // 502 (`coupon_lookup_failed`) y cualquier otro fallo: no es "no existe".
  if (!res.ok || !body) return { status: 'unavailable' };

  // Sin `promotion_code_id` no hay nada que mandarle al checkout, así que un
  // `valid: true` incompleto vale lo mismo que un no.
  if (body.valid !== true || !body.promotion_code_id) return { status: 'invalid' };

  return {
    status: 'valid',
    coupon: {
      code: String(body.code ?? code),
      promotion_code_id: String(body.promotion_code_id),
      percent_off: body.percent_off ?? null,
      amount_off: body.amount_off ?? null,
      currency: body.currency ?? null,
      duration: body.duration ?? 'once',
      duration_in_months: body.duration_in_months ?? null,
      referrer: body.referrer ?? null,
      partner: readCouponPartner(body.partner),
    },
  };
}

/** El socio detrás del cupón, o null si no hay (o si vino mal formado). */
function readCouponPartner(raw: { code?: unknown; partner_name?: unknown } | null | undefined): ValidCoupon['partner'] {
  if (!raw || typeof raw.code !== 'string' || !raw.code) return null;
  const name = typeof raw.partner_name === 'string' ? raw.partner_name.trim() : '';
  return { code: raw.code, partner_name: name || null };
}
